import React, { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";

function karachiToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Karachi" }).format(new Date());
}

function karachiMonth() {
  return karachiToday().slice(0, 7);
}

function messageOf(err, fallback) {
  return err?.response?.data?.message || fallback;
}

function explain(message) {
  return Swal.fire({
    title: "Please check this",
    text: message,
    icon: "warning",
    confirmButtonColor: "#e8002b",
  });
}

function downloadCsv(filename, headers, rows) {
  const lines = [headers.join(",")];
  rows.forEach((row) => {
    lines.push(row.map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const emptyReport = {
  total: 0,
  at_wash: 0,
  ready: 0,
  damaged: 0,
  month_sent: 0,
  month_received: 0,
  month_damaged: 0,
  rows: [],
};

function TowelTag({ kind, children }) {
  const colors = {
    sent: { background: "#fff4d6", color: "#8a5a00" },
    received: { background: "#e5f6ea", color: "#146c36" },
    damaged: { background: "#fde8ea", color: "#9b1c2c" },
  };
  return (
    <span style={{
      ...colors[kind],
      display: "inline-block",
      borderRadius: 999,
      padding: "0.15rem 0.6rem",
      fontWeight: 600,
      fontSize: "0.8rem",
    }}
    >
      {children}
    </span>
  );
}

const ManageTowels = () => {
  const { state } = useContext(NoteState);
  const { role, branch_id: loginBranchId } = state;
  const canPick = String(role) === "1";
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(canPick ? "" : String(loginBranchId || ""));
  const [totalInput, setTotalInput] = useState("");
  const [entryDate, setEntryDate] = useState(karachiToday());
  const [moveType, setMoveType] = useState("sent");
  const [qty, setQty] = useState("");
  const [month, setMonth] = useState(karachiMonth());
  const [report, setReport] = useState(emptyReport);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!canPick) setBranchId(String(loginBranchId || ""));
  }, [canPick, loginBranchId]);

  useEffect(() => {
    const loadBranches = canPick ? UserServices.branchNameAll : UserServices.branchName;
    loadBranches()
      .then((res) => {
        const list = res.data?.data || [];
        setBranches(Array.isArray(list) ? list : []);
      })
      .catch(() => setBranches([]));
  }, [canPick]);

  const load = () => {
    if (!branchId) {
      setReport(emptyReport);
      setTotalInput("");
      return;
    }
    UserServices.towelBoard({ branch_id: branchId, month })
      .then((res) => {
        const data = res.data?.data || emptyReport;
        setReport(data);
        setTotalInput(data.total === 0 || data.total ? String(data.total) : "");
      })
      .catch((err) => Swal.fire("Towels", messageOf(err, "Could not load towels."), "error"));
  };

  useEffect(() => { load(); }, [branchId, month]);

  const branchName = branches.find((row) => String(row.id) === String(branchId))?.name || "This branch";

  const saveStock = async () => {
    if (!branchId) return explain("Choose the branch first.");
    const total = Number(totalInput);
    if (!Number.isInteger(total) || total < 0) return explain("Type how many towels this branch has. Use a whole number.");
    setBusy(true);
    try {
      await UserServices.towelSaveStock({ branch_id: branchId, total });
      load();
      Swal.fire({ title: "Saved", text: `This branch has ${total} towels in total. That number stays until you change it.`, icon: "success", timer: 1800, showConfirmButton: false });
    } catch (err) {
      Swal.fire("Towels", messageOf(err, "Could not save the total."), "error");
    } finally {
      setBusy(false);
    }
  };

  const saveMove = async () => {
    if (!branchId) return explain("Choose the branch first.");
    if (!entryDate) return explain("Choose the date.");
    if (moveType !== "sent" && moveType !== "received" && moveType !== "damaged") return explain("Choose sent for washing, received after wash, or damaged.");
    const count = Number(qty);
    if (!Number.isInteger(count) || count < 1) return explain("Type how many towels. Use a whole number above zero.");
    setBusy(true);
    try {
      await UserServices.towelMove({
        branch_id: branchId,
        entry_date: entryDate,
        type: moveType,
        qty: count,
      });
      setQty("");
      load();
      const text = moveType === "sent"
        ? `${count} towels sent for washing.`
        : moveType === "received"
          ? `${count} towels received after wash.`
          : `${count} towels marked damaged and taken out of ready.`;
      Swal.fire({
        title: "Saved",
        text,
        icon: "success",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire("Towels", messageOf(err, "Could not save."), "error");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    const rows = (report.rows || []).map((row) => [row.date, row.sent, row.received, row.damaged || 0]);
    rows.push(["Total", report.month_sent || 0, report.month_received || 0, report.month_damaged || 0]);
    rows.push([]);
    rows.push(["Total stock", report.total || 0]);
    rows.push(["Out for washing", report.at_wash || 0]);
    rows.push(["Damaged", report.damaged || 0]);
    rows.push(["Ready now", report.ready || 0]);
    downloadCsv(`towels-${branchName}-${month}.csv`, ["Date", "Sent for washing", "Received after wash", "Damaged"], rows);
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="card">
            <div className="card-header py-3">
              <h5 className="mb-0 font-20">Towels</h5>
            </div>
            <div className="card-body">
              <div className="row g-3 align-items-end mb-4">
                <div className="col-md-4">
                  <label className="form-label">Branch</label>
                  {canPick ? (
                    <select className="form-select" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                      <option value="">Choose a branch</option>
                      {branches.map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}
                    </select>
                  ) : (
                    <input className="form-control" value={branchName} disabled />
                  )}
                </div>
              </div>

              {!branchId ? <p className="mb-0">Choose a branch. Then you can set the towels and record washing.</p> : (
                <>
                  <h6>Total towels at this branch</h6>
                  <p className="text-muted small">This number stays the same. Ready towels change when some go for washing, come back, or are damaged.</p>
                  <div className="row g-2 align-items-end mb-4">
                    <div className="col-md-3">
                      <label className="form-label">How many towels</label>
                      <input className="form-control" inputMode="numeric" value={totalInput} onChange={(e) => setTotalInput(e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <button type="button" className="btn btn-primary px-4" disabled={busy} onClick={saveStock}>Save total</button>
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted">Total stock</div>
                        <h3 className="mb-0">{report.total || 0}</h3>
                      </div>
                    </div>
                    <div className="col-md">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted">Out for washing</div>
                        <h3 className="mb-0">{report.at_wash || 0}</h3>
                      </div>
                    </div>
                    <div className="col-md">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted">Damaged</div>
                        <h3 className="mb-0">{report.damaged || 0}</h3>
                      </div>
                    </div>
                    <div className="col-md">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted">Ready now</div>
                        <h3 className="mb-0">{report.ready || 0}</h3>
                      </div>
                    </div>
                    <div className="col-md">
                      <div className="border rounded p-3 h-100">
                        <div className="text-muted">Sent this month</div>
                        <h3 className="mb-0">{report.month_sent || 0}</h3>
                      </div>
                    </div>
                  </div>

                  <h6>Today’s washing</h6>
                  <p className="text-muted small">Choose the date, then choose sent for washing, received after wash, or damaged.</p>
                  <div className="row g-2 align-items-end mb-4">
                    <div className="col-md-3">
                      <label className="form-label">Date</label>
                      <input type="date" className="form-control" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">What happened</label>
                      <select className="form-select" value={moveType} onChange={(e) => setMoveType(e.target.value)}>
                        <option value="sent">Sent for washing</option>
                        <option value="received">Received after wash</option>
                        <option value="damaged">Damaged</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Number of towels</label>
                      <input className="form-control" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value)} />
                    </div>
                    <div className="col-md-3">
                      <button type="button" className="btn btn-primary w-100" disabled={busy} onClick={saveMove}>Save</button>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
                    <div>
                      <h6 className="mb-1">Month report</h6>
                      <div className="text-muted small">Sent this month: {report.month_sent || 0}. Received: {report.month_received || 0}. Damaged: {report.month_damaged || 0}.</div>
                    </div>
                    <div className="d-flex gap-2">
                      <input type="month" className="form-control" value={month} onChange={(e) => setMonth(e.target.value)} />
                      <button type="button" className="btn btn-outline-primary" onClick={download}>Download</button>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped table-borderless table-style-1">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="text-end"><TowelTag kind="sent">Sent</TowelTag></th>
                          <th className="text-end"><TowelTag kind="received">Received</TowelTag></th>
                          <th className="text-end"><TowelTag kind="damaged">Damaged</TowelTag></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(report.rows || []).map((row) => (
                          <tr key={row.date}>
                            <td>{row.date}</td>
                            <td className="text-end">{row.sent ? <TowelTag kind="sent">{row.sent}</TowelTag> : "0"}</td>
                            <td className="text-end">{row.received ? <TowelTag kind="received">{row.received}</TowelTag> : "0"}</td>
                            <td className="text-end">{row.damaged ? <TowelTag kind="damaged">{row.damaged}</TowelTag> : "0"}</td>
                          </tr>
                        ))}
                        {(report.rows || []).length ? (
                          <tr>
                            <td><strong>Total</strong></td>
                            <td className="text-end"><TowelTag kind="sent">{report.month_sent || 0}</TowelTag></td>
                            <td className="text-end"><TowelTag kind="received">{report.month_received || 0}</TowelTag></td>
                            <td className="text-end"><TowelTag kind="damaged">{report.month_damaged || 0}</TowelTag></td>
                          </tr>
                        ) : (
                          <tr><td colSpan="4" className="text-center">Nothing recorded in this month.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManageTowels;
